import React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import { 
    MultistepForm,
    SetSubmitError,
    SetIsLoading,
    InputType,
    FormStep
} from 'view/components/common';
import { 
    normalizeDate, 
    isAgeValid, 
    isRequired, 
    isChained, 
    isEmail, 
    normalizePhone 
} from 'core/utilities';
import { signup, useAuthorizationDispatch } from 'core/context';
import { Palette } from 'core/config';
import { hubspotRegisterForm } from 'core/api';

type FormValues = {
    first_name   : string;
    last_name    : string;
    dob          : string;
    full_address : string;
    address      : string;
    suite        : string;
    city         : string;
    region       : string;
    country      : string;
    postal       : string;
    email        : string;
    password     : string;
    phone        : string;
    own          : boolean;
};

const initialValues = {
    first_name   : '',
    last_name    : '',
    dob          : '',
    full_address : '',
    address      : '',
    suite        : '',
    city         : '',
    region       : '',
    country      : '',
    postal       : '',
    email        : '',
    password     : '',
    phone        : '',
    own          : false
};

const formSteps = [
    [
        {
            type : InputType.text,
            name : 'first_name',
            label : 'First Name',
            placeholder : 'First',
            validator : isRequired
        },
        {
            type : InputType.text,
            name : 'last_name',
            label : 'Last Name',
            placeholder : 'Last',
            validator : isRequired
        },
        {
            type : InputType.text,
            name : 'dob',
            label : 'Birthday',
            placeholder : 'yyyy/mm/dd',
            normalizer : normalizeDate,
            validator : isChained([ isAgeValid, isRequired ])
        }
    ],
    [
        {
            type : InputType.location,
            name : 'full_address',
            label : 'Address',
            placeholder : '100 King Street West, Toronto, ON, Canada',
            validator : isRequired
        },
        {
            type : InputType.toggle,
            name : 'own',
            label : 'Do you own this property?'
        },
        {
            type : InputType.info,
            Component : () => (<br/>)
        }
    ],
    [
        {
            type : InputType.text,
            name : 'phone',
            label : 'Mobile Number',
            placeholder : '(123) 456-7890',
            normalizer : normalizePhone,
            validator : isRequired
        },
        {
            type : InputType.email,
            name : 'email',
            label : 'Email Address',
            placeholder : 'salaam@manzil.ca',
            validator : isChained([ isEmail, isRequired ])
        },
        {
            type : InputType.password,
            name : 'password',
            label : 'Password',
            placeholder : '************',
            validator : isRequired
        },
        {
            type : InputType.info,
            Component : () => (<br/>)
        }
    ]
] as Array<FormStep>;

export const RegisterAccountPage : React.FunctionComponent = () =>
{
    const history = useHistory();
    const dispatch = useAuthorizationDispatch();

    const onSubmit = (setSubmitError: SetSubmitError, setIsLoading : SetIsLoading) =>
    {
        return async (payload : FormValues) => 
        {
            const {
                first_name,
                last_name,
                address,
                full_address,
                suite,
                city,
                region,
                country,
                postal,
                phone,
                password,
                own
            } = payload;

            let {
                email,
                dob
            } = payload;
    
            email = email.toLowerCase();
            dob = dob.split('/').join('-');

            // Deprecated value required by backend
            const citizenship = '';

            setSubmitError(undefined);
            setIsLoading(true);
    
            const {
                success,
                error
            } = await signup({ 
                dispatch, 
                payload: {
                    user : {
                        email,
                        password,
                        first_name,
                        last_name,
                        phone,
                        citizenship,
                        dob
                    },
                    user_address : {
                        address,
                        suite,
                        city,
                        region,
                        country,
                        postal,
                        own
                    }
                } 
            });
    
            if (success)
            {
                const portalId = '6138734';
                const formId = '6d86402c-64cb-487c-ab15-44bd374e220d';

                const body = {
                    portalId,
                    formId,
                    fields: [
                        {
                            name: 'firstname',
                            value: first_name
                        },
                        {
                            name: 'lastname',
                            value: last_name
                        },
                        {
                            name: 'date_of_birth',
                            value: dob
                        },
                        {
                            name: 'address',
                            value: full_address
                        },
                        {
                            name: 'mobilephone',
                            value: phone
                        },
                        {
                            name: 'email',
                            value: email
                        },
                        {
                            name: 'do_you_own_this_property_',
                            value: own
                        },
                    ]
                };
                
                await hubspotRegisterForm({ portalId, formId, body });

                history.push('/dashboard');
            }
            else 
            {
                setIsLoading(false);
                setSubmitError(error);
            }
        };
    };

    const onExit = () => history.push('/');

    return (
        <MultistepForm
            title='Create your Manzil Account'
            stepTitle={(step: number) => `${step + 1}/3`}
            onExit={onExit}
            initialValues={initialValues}
            formSteps={formSteps}
            onSubmit={onSubmit}
        >
            <Link>
                Already have an account? <span onClick={onExit}>Log in</span>
            </Link>
        </MultistepForm>
    );
};

const Link = styled.div`
    font: normal normal normal 10px 'SofiaPro-Regular';
    color: ${Palette.BLUE};
    user-select: none;
    text-align: center;
    margin-top: 15px;

    @media only screen and (min-width: 1300px) {
        font-size: 12px;
    }

    @media only screen and (min-width: 1900px) {
        font-size: 14px;
    }

    span {
        cursor: pointer;
        text-decoration: underline;
    }
`;